import React, {
    useContext, useState, useEffect, useCallback, useMemo
} from 'react';
import { Switch, Route } from 'react-router-dom';
import _ from 'lodash';
// Imported First to allow Jutro styles to be overridden
import './App.scss';
import { setComponentMapOverrides } from '@jutro/uiconfig';
import { ModalNextProvider } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { DropdownMenuLink } from '@jutro/router';
import { AppFloorPlan } from '@jutro/floorplan';
import { DynamicRoute, useAuthentication, withAuthContextProvider } from 'gw-digital-auth-react';
import vmTranslator from 'gw-platform-translations';
import { ViewModelServiceFactory } from 'gw-portals-viewmodel-js';
import { AccurateBreakpointPropagation } from 'gw-jutro-adapters-react';
import { LoginCapabilityComponentMap, LoginCapabilityComponents } from 'gw-portals-login-react';
import { AdditionalEnrollment, SignUpEnrollment } from 'gw-capability-enrollment-react';
import { WizardPageTemplateWithTitle } from 'gw-portals-wizard-react';
import { filterCapabilityRoutes } from 'gw-portals-config-js';

import { DependencyProvider } from 'gw-portals-dependency-react';

import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import {
    platformComponentMap,
    platformComponents,
    ImageComponent,
} from 'gw-components-platform-react';
import { ErrorBoundary } from 'gw-portals-error-react';
import BaseFNOLWizard, { ClaimsConfirmationPage } from 'gw-capability-fnol-common-react';
import { ContactAgentPage } from 'gw-pages-platform-react';
import FNOLPAWizard from 'gw-capability-fnol-pa-react';
import FNOLWCWizard from 'gw-capability-fnol-wc-react';
import FNOLCAWizard from 'gw-capability-fnol-ca-react';
import FNOLHOWizard from 'gw-capability-fnol-ho-react';
import FNOLCPWizard from 'gw-capability-fnol-cp-react';
import FNOLBOPWizard from 'gw-capability-fnol-bop-react';
import FNOLGeneralWizard from 'gw-capability-fnol-gl-react';
import { EnrollmentService } from 'gw-capability-claimenrollment';
import 'gw-capability-producer-fnol-common-react';
import { ClaimDocumentService } from 'gw-capability-claimdocument';
import { AddressLookupService } from 'gw-capability-addresslookup';
import {
    ClaimList, ClaimDetails, CreateNote, PolicyLevelCoverages, ApprovedRepairer
} from 'gw-capability-claim-react';
import { ClaimService } from 'gw-capability-claim';
import { FNOLService } from 'gw-capability-fnol';
import FaqPage from 'gw-capability-faq-react';
import { PolicyDocumentError } from 'gw-capability-document-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import ResetPasswordPage from '../pages/ResetPasswordPage/ResetPasswordPage';
import EntryPage from '../pages/EntryPage/EntryPage';
import LandingPage from '../pages/LandingPage/LandingPage';
import messages from './App.messages';
import appRoutes from './App.routes.metadata.json5';
import getPreLoadInfo, { setPreLoadInfo } from './preLoadInfo';
import AnalyticsTrackerUtil from './AnalyticsTrackerUtil';
const { capabilitiesConfig, env } = appConfig;

const componentMap = {
    landingpage: LandingPage,
    additionalenrollmentpage: AdditionalEnrollment,
    basefnolwizard: BaseFNOLWizard,
    claimdetails: ClaimDetails,
    claimlist: ClaimList,
    claimsconfirmationpage: ClaimsConfirmationPage,
    contactagent: ContactAgentPage,
    createnote: CreateNote,
    approvedrepairer: ApprovedRepairer,
    faqpage: FaqPage,
    fnolbopwizard: FNOLBOPWizard,
    fnolcawizard: FNOLCAWizard,
    fnolcpwizard: FNOLCPWizard,
    fnolgeneralwizard: FNOLGeneralWizard,
    fnolhowizard: FNOLHOWizard,
    fnolpawizard: FNOLPAWizard,
    fnolwcwizard: FNOLWCWizard,
    policydocumenterror: PolicyDocumentError,
    policylevelcoverages: PolicyLevelCoverages
};

setComponentMapOverrides(
    {
        ...platformComponentMap,
        ...LoginCapabilityComponentMap,
        img: { component: 'img' },
        WizardPageTemplateWithTitle: { component: 'WizardPageTemplateWithTitle' },
    },
    {
        ...platformComponents,
        ...LoginCapabilityComponents,
        img: ImageComponent,
        WizardPageTemplateWithTitle,
    }
);

function App() {
    const auth = useAuthentication();
    const translator = useContext(TranslatorContext);
    const [viewModelService, setViewModelService] = useState(undefined);
    const [routesWithoutTitle, setRoutesWithoutTitle] = useState(undefined);

    // <DropdownMenuLink onClick={auth.logout}>{translator(messages.logout)}</DropdownMenuLink>

    const getAvatarLinks = useCallback(() => {
        return [
            <DropdownMenuLink to="/faq">{translator(messages.faq)}</DropdownMenuLink>,
        ];
    }, [translator, auth]);

    const getLogoSrc = useCallback(() => {
        const prefix = _.get(env, 'DEPLOYMENT_PATH', '').replace(/\/$/, '');
        //TODO Pandia
        return `${prefix}/branding/allianz-logo.svg`;
        //return `/branding/allianz-logo.svg`;
    }, []);


    const floorPlansConfig = useMemo(() => {
        return {
            scrollContent: true,
            routes: routesWithoutTitle,
            showNav: 'top',
            header: {
                useAuthInfo: false,
                avatarChildren: getAvatarLinks(),
                avatarProps: {
                    username: auth.user_name,
                    title: auth.name,
                    subtitle: auth.user_name
                },
                className: 'appheader',
                logoSrc: getLogoSrc(),
                logoTitle: '| Claims Hub',
                showAvatar: false,
                showAppSwitcher: false,
                showNotifications: false
            }
        };
    }, [auth.name, auth.user_name, getAvatarLinks, getLogoSrc, routesWithoutTitle]);

    useEffect(() => {
        const translatorFn = vmTranslator(translator);
        AnalyticsTrackerUtil.addAnalyticsElements();
        import(
            /* webpackChunkName: "product-metadata" */
            // eslint-disable-next-line import/no-unresolved
            'product-metadata'
        ).then((productMetadata) => {
            const { default: result } = productMetadata;
            setViewModelService(
                ViewModelServiceFactory.getViewModelService(result, translatorFn)
            );
        });
        const initrouteValues = filterCapabilityRoutes(capabilitiesConfig, appRoutes.routes)
            .map((route) => {
                return {
                    ...route,
                    title: ''
                };
            });
        setRoutesWithoutTitle(initrouteValues);
        const preLoadInfo = getPreLoadInfo(auth.authHeader);
        if (preLoadInfo.fetchStatus) {
            preLoadInfo.promise.then((responseData) => {
                setPreLoadInfo(responseData);
                const routesInPreload = responseData.usefulLinks;
                if (routesInPreload && routesInPreload.length > 0) {
                    const routeValues = filterCapabilityRoutes(capabilitiesConfig, appRoutes.routes)
                        .map((route) => {
                            if (route.routes) {
                                return {
                                    ...route,
                                    title: '',
                                    showOnNavBar: true,
                                    routes: routesInPreload
                                };
                            }
                            return {
                                ...route,
                                title: ''
                            };
                        });
                    setRoutesWithoutTitle(routeValues);
                }
            }).catch((error) => {
            });
        }
    }, [translator]);

    const handleError = useCallback((error) => {
        ModalNextProvider.showAlert({
            status: 'error',
            icon: 'mi-error-outline',
            title: 'An Error',
            message: error.message,
        }).then((result) => {
            if (result === 'confirm') {
                // TODO handle click on confirm
            }
        }).catch(_.noop);
    }, []);

    return (
        <AccurateBreakpointPropagation>
            <DependencyProvider
                value={{
                    FNOLService: FNOLService,
                    ClaimService: ClaimService,
                    ClaimDownloadService: ClaimDocumentService,
                    EnrollmentService: EnrollmentService,
                    AddressLookupService: AddressLookupService
                }}
            >
                <ViewModelServiceContext.Provider value={viewModelService}>
                    <ModalNextProvider />
                    <ErrorBoundary onError={handleError}>
                        <Switch>
                            <Route exact path="/login-page" component={EntryPage} />
                            <DynamicRoute path="/">
                                <AppFloorPlan
                                    componentMap={componentMap}
                                    {...floorPlansConfig}
                                />
                            </DynamicRoute>
                        </Switch>
                    </ErrorBoundary>
                </ViewModelServiceContext.Provider>
            </DependencyProvider>
        </AccurateBreakpointPropagation>
    );
}

export default withAuthContextProvider()(App);
